import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { startPointsApi, StartPoint } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getNationFromCoordinates } from '../../utils/geoUtils';
import Modal from '../Modal/Modal';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { CoordinatePicker } from '../CoordinatePicker/CoordinatePicker';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import './CreateExitHubModal.css';

interface CreateExitHubModalProps {
  onClose: () => void;
  onCreated: (startPoint: StartPoint) => void;
}

function CreateExitHubModal({ onClose, onCreated }: CreateExitHubModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showNationConfirm, setShowNationConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    types: [] as ('AIRPORT' | 'TRAIN_STATION' | 'SEAPORT' | 'LOCATION')[],
    operator: '',
    contactDetails: '',
    nation: user?.nation || '',
    latitude: 0,
    longitude: 0,
    description: '',
  });

  const handleCoordinateChange = (lat: number, lng: number) => {
    const roundedLat = Math.round(lat * 1000000) / 1000000;
    const roundedLng = Math.round(lng * 1000000) / 1000000;
    const detectedNation = getNationFromCoordinates(roundedLat, roundedLng);
    setFormData(prev => ({
      ...prev,
      latitude: roundedLat,
      longitude: roundedLng,
      nation: detectedNation,
    }));
  };

  const handleTypeToggle = (type: 'AIRPORT' | 'TRAIN_STATION' | 'SEAPORT' | 'LOCATION') => {
    setFormData(prev => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter(t => t !== type)
        : [...prev.types, type],
    }));
  };

  const doSubmit = async (nationOverride?: string) => {
    const submitData = nationOverride ? { ...formData, nation: nationOverride } : formData;

    try {
      setSaving(true);
      const response = await startPointsApi.create({
        ...submitData,
        ucpmClassification: 'UCPM_EXIT_HUB' as 'UCPM_EXIT_HUB',
        isActive: true,
      });
      onCreated(response.data);
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } };
      setError(apiErr.response?.data?.error || 'Failed to create exit hub');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.types.length === 0) {
      setError('Please select at least one hub type');
      return;
    }

    if (formData.latitude === 0 && formData.longitude === 0) {
      setError('Please select a location on the map');
      return;
    }

    if (user?.role === 'NATION') {
      if (formData.nation && formData.nation !== user.nation) {
        // Clearly detected as a different nation → block
        setError(`Hub must be located in your nation (${user.nation}). The selected coordinates are in "${formData.nation}".`);
        return;
      }
      if (!formData.nation) {
        // Nation could not be detected (e.g. island) → ask user to confirm
        setShowNationConfirm(true);
        return;
      }
    }

    await doSubmit();
  };

  const handleNationConfirmProceed = async () => {
    setShowNationConfirm(false);
    await doSubmit(user?.nation || '');
  };

  return (
    <Modal
      title={t('requests.createExitHub')}
      isOpen={true}
      onClose={onClose}
      width="95%"
      maxWidth="1600px"
      maxHeight="95vh"
    >
      <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
        <LoadingOverlay loading={saving} text={`${t('common.loading')}`} />
        {error && <div className="error" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>}

        <div className="hub-form-split">
          <div className="hub-form-left">
            <div className="form-group">
              <label htmlFor="ceh-name">{t('common.name')} <span className="required">*</span></label>
              <input
                type="text"
                id="ceh-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Berlin Brandenburg Airport"
              />
            </div>

            <div className="form-group">
              <label>
                {t('common.type')} <span className="required">*</span>
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'normal', marginLeft: 'var(--space-2)', color: 'var(--text-muted)' }}>
                  ({t('common.select')})
                </span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2-5)', marginTop: 'var(--space-2)' }}>
                {(['AIRPORT', 'TRAIN_STATION', 'SEAPORT', 'LOCATION'] as const).map(type => (
                  <label key={type} className="checkbox-label-inline">
                    <input
                      type="checkbox"
                      checked={formData.types.includes(type)}
                      onChange={() => handleTypeToggle(type)}
                    />
                    <span>{type === 'TRAIN_STATION' ? 'Train Station' : type === 'LOCATION' ? 'Bus Station' : type.charAt(0) + type.slice(1).toLowerCase()}</span>
                  </label>
                ))}
              </div>
              {formData.types.length === 0 && (
                <p className="sp-validation-hint">Please select at least one type</p>
              )}
            </div>

            <div className="form-group">
              <label>{t('hubs.ucpmClassification')}</label>
              <input type="text" value="UCPM Exit Hub" disabled style={{ backgroundColor: 'var(--bg-inset)', cursor: 'not-allowed' }} />
              <p className="sp-nation-hint">Nations can only create UCPM Exit Hubs</p>
            </div>

            <div className="form-group">
              <label htmlFor="ceh-operator">{t('assets.operator')}</label>
              <input
                type="text"
                id="ceh-operator"
                value={formData.operator}
                onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                placeholder="e.g., Berlin Airports"
              />
            </div>

            <div className="form-group">
              <label htmlFor="ceh-contact">{t('hospitals.contactPerson')}</label>
              <textarea
                id="ceh-contact"
                value={formData.contactDetails}
                onChange={(e) => setFormData({ ...formData, contactDetails: e.target.value })}
                placeholder="Phone, Email, Address, etc."
                rows={2}
                style={{ resize: 'vertical' }}
              />
            </div>

            <div className="form-group">
              <label>{t('common.nation')}</label>
              <input
                type="text"
                value={formData.nation}
                disabled
                placeholder="Select coordinates on map"
                style={{ backgroundColor: 'var(--bg-inset)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ceh-lat">{t('common.latitude')} <span className="required">*</span></label>
                <input
                  type="number"
                  id="ceh-lat"
                  step="0.000001"
                  min="-90"
                  max="90"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="ceh-lng">{t('common.longitude')} <span className="required">*</span></label>
                <input
                  type="number"
                  id="ceh-lng"
                  step="0.000001"
                  min="-180"
                  max="180"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="ceh-desc">{t('common.description')}</label>
              <textarea
                id="ceh-desc"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Additional details about this location..."
              />
            </div>

            <div className="sp-info-box">
              <strong><Info size={14} style={{ verticalAlign: 'middle', marginRight: 'var(--space-1)' }} /> Note:</strong>
              <p>This exit hub will be created for your nation and immediately available for selection in your offer.</p>
            </div>
          </div>

          <div className="hub-form-right">
            <div className="form-group">
              <label>{t('components.coordinatePicker.pickLocation')}</label>
              <CoordinatePicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                onCoordinateChange={handleCoordinateChange}
                height="100%"
              />
              <p className="form-hint">{t('components.coordinatePicker.clickToSelect')}</p>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={saving}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? `${t('common.loading')}` : t('requests.createExitHub')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showNationConfirm}
        title="Location Not Recognized"
        message={`The selected coordinates (${formData.latitude}, ${formData.longitude}) could not be assigned to any nation. This can happen for locations on islands or near borders.`}
        warning={`The hub will be created for your nation (${user?.nation || ''}).`}
        hint="Are you sure you want to proceed?"
        confirmLabel="Yes, Create Hub"
        cancelLabel={t('common.cancel')}
        confirmVariant="primary"
        onConfirm={handleNationConfirmProceed}
        onCancel={() => setShowNationConfirm(false)}
      />
    </Modal>
  );
}

export default CreateExitHubModal;
